import type { PhaseStatus, SseEvent } from "../types/api";

const SUPERVISOR_TOOLS = new Set([
  "rewrite_query_tool",
  "classify_query_tool",
  "deduplicate_papers_tool",
  "enrich_citations_tool",
  "generate_followup_tool",
  "cci_estimate_tool",
]);

const RETRIEVE_TOOLS = new Set([
  "deduplicate_papers_tool",
  "enrich_citations_tool",
  "generate_followup_tool",
  "cci_estimate_tool",
]);

export interface PhaseContext {
  phaseStatus: PhaseStatus[];
  phaseStartedAt: (number | null)[];
  phaseElapsedMs: (number | null)[];
  phase3WasActive: boolean;
  now: number;
}

function idx(n: number): number {
  return n - 1;
}

function freezePhase(ctx: PhaseContext, phaseIndex: number): void {
  const i = idx(phaseIndex);
  if (ctx.phaseStatus[i] === "done" || ctx.phaseStatus[i] === "error") return;
  ctx.phaseStatus[i] = "done";
  if (ctx.phaseStartedAt[i] != null && ctx.phaseElapsedMs[i] == null) {
    ctx.phaseElapsedMs[i] = ctx.now - ctx.phaseStartedAt[i]!;
  }
}

export function activatePhase(ctx: PhaseContext, phaseNum: number): void {
  const i = idx(phaseNum);
  if (ctx.phaseStatus[i] === "done" || ctx.phaseStatus[i] === "error") return;

  for (let p = 1; p < phaseNum; p++) {
    if (ctx.phaseStatus[idx(p)] !== "done" && ctx.phaseStatus[idx(p)] !== "error") {
      freezePhase(ctx, p);
    }
  }

  if (ctx.phaseStatus[i] !== "active") {
    ctx.phaseStatus[i] = "active";
    if (ctx.phaseStartedAt[i] == null) {
      ctx.phaseStartedAt[i] = ctx.now;
    }
  }

  if (phaseNum === 3) ctx.phase3WasActive = true;
  if (phaseNum === 4) freezePhase(ctx, 3);
  if (phaseNum === 5) freezePhase(ctx, 4);
}

export function doneAllPhases(ctx: PhaseContext): void {
  for (let p = 1; p <= 5; p++) {
    freezePhase(ctx, p);
  }
}

function isSpecialistTool(name: string | undefined): boolean {
  if (!name) return false;
  if (name.endsWith("_specialist")) return true;
  return !SUPERVISOR_TOOLS.has(name);
}

export function phaseFromEvent(event: SseEvent, ctx: PhaseContext): void {
  const name = event.name;

  switch (event.type) {
    case "tool_start":
    case "tool_running":
      if (name === "rewrite_query_tool") activatePhase(ctx, 1);
      else if (name === "classify_query_tool") activatePhase(ctx, 2);
      else if (name && RETRIEVE_TOOLS.has(name)) activatePhase(ctx, 4);
      else if (isSpecialistTool(name)) activatePhase(ctx, 3);
      break;
    case "tool_result":
      if (name && RETRIEVE_TOOLS.has(name)) activatePhase(ctx, 4);
      break;
    case "specialist_tool":
    case "specialist_papers":
    case "specialist_text":
      activatePhase(ctx, 3);
      break;
    case "run_start":
      activatePhase(ctx, 1);
      break;
    case "query_rewritten":
      freezePhase(ctx, 1);
      break;
    case "query_classified":
      freezePhase(ctx, 2);
      break;
    case "text":
      if (ctx.phase3WasActive) activatePhase(ctx, 5);
      break;
    case "consensus":
      activatePhase(ctx, 5);
      break;
    case "run_end":
      doneAllPhases(ctx);
      break;
    default:
      break;
  }
}

export { SUPERVISOR_TOOLS, RETRIEVE_TOOLS, isSpecialistTool };
